/**
 * Container Management Validators
 *
 * Composite validation functions that combine multiple validators.
 * Use these for complete validation before container operations.
 */

import type { ContainerAPI } from '../../types/api.types';
import type { ValidationResult, ValidationError, ValidationWarning } from '../types/container-row.types';
import { CONTAINER_CONSTRAINTS, type ContainerConstraints } from '../constants/constraints';
import { validateRowConstraint as _validateRowConstraint } from './rowConstraintValidator';
import { validateNestingDepth as _validateNestingDepth } from './nestingValidator';
import { isValidWidth, getTotalWidth } from '../utils/widthCalculations';
import { getContainersInRow } from '../utils/rowCalculations';

// Re-export individual validators
export {
  validateRowConstraint,
  validateCanAddToRow,
  findFirstAvailableRow,
  validateRowAfterOperation,
} from './rowConstraintValidator';

export {
  validateNestingDepth,
  validateNoCircularNesting,
  validateCanNest,
  validateNestingOperation,
} from './nestingValidator';

/**
 * Comprehensive validation for adding a container
 *
 * Validates:
 * - Row constraint (max 3 per row)
 * - Nesting depth (max 2 levels)
 * - Width validity
 *
 * @param containers - All containers in the canvas
 * @param rowIndex - Target row index
 * @param parentId - Parent container ID (null for top-level)
 * @param width - Proposed width
 * @param constraints - Container constraints (optional)
 * @returns Comprehensive validation result
 */
export const validateAddContainer = (
  containers: ContainerAPI[],
  rowIndex: number,
  parentId: string | null,
  width: string,
  constraints: ContainerConstraints = CONTAINER_CONSTRAINTS
): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Validate row constraint
  const rowResult = _validateRowConstraint(containers, rowIndex, constraints);
  errors.push(...rowResult.errors);

  // Validate nesting depth
  const nestingResult = _validateNestingDepth(containers, parentId, constraints);
  errors.push(...nestingResult.errors);

  // Validate width
  if (!isValidWidth(width)) {
    errors.push({
      code: 'INVALID_WIDTH',
      message: `Width "${width}" is invalid. Must be between ${constraints.MIN_WIDTH_PERCENT}% and ${constraints.MAX_WIDTH_PERCENT}%.`,
      context: {
        width,
        minAllowed: constraints.MIN_WIDTH_PERCENT,
        maxAllowed: constraints.MAX_WIDTH_PERCENT,
      },
    });
  }

  // Warning: Check if total width would exceed 100%
  const rowContainers = getContainersInRow(containers, rowIndex);
  const currentTotalWidth = getTotalWidth(rowContainers);
  const proposedWidth = parseFloat(width.replace('%', '')) || 0;

  if (currentTotalWidth + proposedWidth > 100) {
    warnings.push({
      code: 'WIDTH_OVERFLOW',
      message: `Adding this container would result in ${currentTotalWidth + proposedWidth}% total width in row ${rowIndex}.`,
      context: {
        currentTotalWidth,
        proposedWidth,
        resultingWidth: currentTotalWidth + proposedWidth,
      },
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Validates container move operation
 *
 * @param containers - All containers in the canvas
 * @param containerId - Container being moved
 * @param targetRowIndex - Target row index
 * @param targetParentId - Target parent ID (null for top-level)
 * @param constraints - Container constraints (optional)
 * @returns Validation result
 */
export const validateMoveContainer = (
  containers: ContainerAPI[],
  containerId: string,
  targetRowIndex: number,
  targetParentId: string | null,
  constraints: ContainerConstraints = CONTAINER_CONSTRAINTS
): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Find current container
  const container = containers.find((c) => c.id === containerId);
  if (!container) {
    errors.push({
      code: 'CONTAINER_NOT_FOUND',
      message: `Container "${containerId}" not found.`,
      context: { containerId },
    });
    return { isValid: false, errors, warnings };
  }

  // Exclude moving container from row count check
  const containersWithoutMoving = containers.filter((c) => c.id !== containerId);
  const rowResult = _validateRowConstraint(containersWithoutMoving, targetRowIndex, constraints);
  errors.push(...rowResult.errors);

  // Validate nesting if moving to nested position
  if (targetParentId) {
    const nestingResult = _validateNestingDepth(containers, targetParentId, constraints);
    errors.push(...nestingResult.errors);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Validates removing a container
 *
 * @param containers - All containers in the canvas
 * @param containerId - Container to remove
 * @returns Validation result
 */
export const validateRemoveContainer = (
  containers: ContainerAPI[],
  containerId: string
): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  const container = containers.find((c) => c.id === containerId);
  if (!container) {
    errors.push({
      code: 'CONTAINER_NOT_FOUND',
      message: `Container "${containerId}" not found.`,
      context: { containerId },
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Combines multiple validation results
 *
 * @param results - Array of validation results
 * @returns Combined validation result
 */
export const combineValidationResults = (
  ...results: ValidationResult[]
): ValidationResult => {
  const allErrors: ValidationError[] = [];
  const allWarnings: ValidationWarning[] = [];

  for (const result of results) {
    allErrors.push(...result.errors);
    allWarnings.push(...result.warnings);
  }

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
  };
};

/**
 * Creates a validation error
 */
export const createValidationError = (
  code: ValidationError['code'],
  message: string,
  context?: Record<string, unknown>
): ValidationError => ({
  code,
  message,
  context,
});

/**
 * Creates a validation warning
 */
export const createValidationWarning = (
  code: ValidationWarning['code'],
  message: string,
  context?: Record<string, unknown>
): ValidationWarning => ({
  code,
  message,
  context,
});

/**
 * Creates a successful validation result
 */
export const validationSuccess = (): ValidationResult => ({
  isValid: true,
  errors: [],
  warnings: [],
});

/**
 * Creates a failed validation result
 */
export const validationFailure = (
  errors: ValidationError[],
  warnings: ValidationWarning[] = []
): ValidationResult => ({
  isValid: false,
  errors,
  warnings,
});
